import React, { useContext, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { Box, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useNav } from "../../context/NavContext";
import TradesTable from "../table/TradesTable";
import PositionsTable from "../table/PositionsTable";
import PortfolioAlerts from "../PortfolioAlerts";
import { PositionsDataContext } from "../../context/PositionsDataContext";
import {
  useFilterState,
  useScrollPosition,
  useSortingState,
  useTabState,
} from "../../hooks/useScrollPosition";
import {
  calculateBasisPoints,
  calculateNavPercentage,
  calculateStrategyMetrics,
  formatBasisPoints,
  formatPercentage,
  getPortfolioAlertsCount,
  getPositionsWithTrades,
} from "../../utils/dataCalculations";

// Import modular UI components
import {
  CardWrapper,
  GridContainer,
  MetricCard,
  PageHeader,
  Section,
  SummaryBox,
  TabNavigation,
} from "../ui";

const Home = () => {
  // ===== STATE =====
  const theme = useTheme();
  const { datasets } = useContext(PositionsDataContext);
  const NAV_NUMBER = useNav();

  // Data states
  const [allData, setAllData] = useState([]);
  const [previousData, setPreviousData] = useState([]);
  const [tradedData, setTradedData] = useState([]);
  const [summaryData, setSummaryData] = useState({
    firmwide: {
      totalPnl: 0,
      totalLmv: 0,
      totalSmv: 0,
      winners: [],
      losers: [],
    },
    mab: { totalPnl: 0, totalLmv: 0, totalSmv: 0, winners: [], losers: [] },
    credit: { totalPnl: 0, totalLmv: 0, totalSmv: 0, winners: [], losers: [] },
    spac: { totalPnl: 0, totalLmv: 0, totalSmv: 0, winners: [], losers: [] },
    event: { totalPnl: 0, totalLmv: 0, totalSmv: 0, winners: [], losers: [] },
    creditArb: {
      totalPnl: 0,
      totalLmv: 0,
      totalSmv: 0,
      winners: [],
      losers: [],
    },
    rest: { totalPnl: 0, totalLmv: 0, totalSmv: 0, winners: [], losers: [] },
  });
  const [portfolioAlertsCount, setPortfolioAlertsCount] = useState(0);

  // Custom hooks for state management
  const [activeTab, setActiveTab] = useTabState("homeActiveTab", "tab1");
  // An alert notification's "Open" stores the tab then navigates here; location.key
  // changes on every navigation, so this also switches tabs when Home is already showing.
  const location = useLocation();
  useEffect(() => {
    try {
      const saved = localStorage.getItem("homeActiveTab");
      if (saved) setActiveTab(saved);
    } catch {
      // Keep the current tab.
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key]);
  const shownTab = ["tab1", "tab2", "tab3", "tab4"].includes(activeTab) ? activeTab : "tab1";
  const handleTabChange = (newTab) => {
    setActiveTab(newTab);
  };
  const [GlobalFilterPositions, setGlobalFilterPositions] = useFilterState(
    "homePositionsTableGlobalFilter",
    ""
  );
  const [tradesGlobalFilter, setTradesGlobalFilter] = useFilterState(
    "homeTradesTableGlobalFilter",
    ""
  );
  const [positionsSorting, setPositionsSorting] = useSortingState(
    "positionsTableSorting",
    [
      {
        id: "StrategyName",
        desc: false,
      },
    ]
  );
  const [tradesSorting, setTradesSorting] = useSortingState(
    "tradesTableSorting",
    [
      {
        id: "StrategyType",
        desc: false,
      },
    ]
  );

  // Scroll position management
  const positionsScroll = useScrollPosition(
    "positionsTableScrollPosition",
    0,
    '[class*="MuiTableContainer-root"]'
  );
  const tradesScroll = useScrollPosition(
    "tradesTableScrollPosition",
    0,
    '[class*="MuiTableContainer-root"]'
  );
  const portfolioAlertsScroll = useScrollPosition(
    "portfolioAlertsScrollPosition",
    0
  );

  // ===== CONFIG =====
  const tabs = [
    { value: "tab1", label: "Firmwide Summary" },
    { value: "tab2", label: "All Trades" },
    { value: "tab3", label: "All Positions" },
    { value: "tab4", label: "Portfolio Alerts" },
  ];

  // ===== EFFECTS =====
  useEffect(() => {
    if (datasets?.positions) setAllData(datasets.positions);
    if (datasets?.previousDay) setPreviousData(datasets.previousDay);
  }, [datasets]);

  useEffect(() => {
    if (allData) {
      try {
        const firmwideMetrics = calculateStrategyMetrics(allData, "firmwide");
        const creditPositions = allData?.filter((position) =>
          [
            "Called or Tendered",
            "Called or Tendered ",
            "Expected Call",
            "RED",
            "Yield to Maturity",
          ].includes(position.StrategyType)
        );

        const summary = {
          firmwide: firmwideMetrics,
          mab: calculateStrategyMetrics(allData, "Merger Arbitrage"),
          credit: calculateStrategyMetrics(creditPositions, "credit"),
          spac: calculateStrategyMetrics(allData, "SPAC"),
          event: calculateStrategyMetrics(allData, "Convertible Arbitrage"),
          creditArb: calculateStrategyMetrics(allData, "Credit Arbitrage"),
          rest: calculateStrategyMetrics(
            allData?.filter(
              (position) =>
                ![
                  "Merger Arbitrage",
                  "SPAC",
                  "Convertible Arbitrage",
                  "Credit Arbitrage",
                  "Called or Tendered",
                  "Called or Tendered ",
                  "Expected Call",
                  "RED",
                  "Yield to Maturity",
                ].includes(position.StrategyType)
            ),
            "rest"
          ),
        };
        setSummaryData(summary);
      } catch (error) {
        console.error("Error calculating summary data:", error);
      }
    }
  }, [allData, previousData]);

  useEffect(() => {
    if (allData?.length > 0) {
      const positionsWithTrades = getPositionsWithTrades(allData);
      setTradedData(positionsWithTrades);
    }
  }, [allData]);

  useEffect(() => {
    setPortfolioAlertsCount(
      getPortfolioAlertsCount(allData, previousData, NAV_NUMBER)
    );
  }, [allData, previousData, NAV_NUMBER]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (activeTab === "tab1") {
        window.addEventListener("scroll", portfolioAlertsScroll.handleScroll);
        return () =>
          window.removeEventListener(
            "scroll",
            portfolioAlertsScroll.handleScroll
          );
      } else if (activeTab === "tab2") {
        const container = document.querySelector(
          '[class*="MuiTableContainer-root"]'
        );
        if (container) {
          container.addEventListener("scroll", tradesScroll.handleScroll);
          return () =>
            container.removeEventListener("scroll", tradesScroll.handleScroll);
        }
      } else if (activeTab === "tab3") {
        const container = document.querySelector(
          '[class*="MuiTableContainer-root"]'
        );
        if (container) {
          container.addEventListener("scroll", positionsScroll.handleScroll);
          return () =>
            container.removeEventListener(
              "scroll",
              positionsScroll.handleScroll
            );
        }
      }
    }, 100);
    return () => clearTimeout(timer);
  }, [activeTab, portfolioAlertsScroll, tradesScroll, positionsScroll]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (activeTab === "tab1") {
        portfolioAlertsScroll.restoreScrollPosition();
      } else if (activeTab === "tab2") {
        tradesScroll.restoreScrollPosition();
      } else if (activeTab === "tab3") {
        positionsScroll.restoreScrollPosition();
      }
    }, 150);
    return () => clearTimeout(timer);
  }, [activeTab, portfolioAlertsScroll, tradesScroll, positionsScroll]);

  useEffect(() => {
    const savedFilter = localStorage.getItem("allPositionsFilter");
    if (savedFilter && !GlobalFilterPositions) {
      setGlobalFilterPositions(savedFilter);
    }
  }, [GlobalFilterPositions, setGlobalFilterPositions]);

  // ===== MEMOIZED VALUES =====
  // (No memoized values in this component)

  // ===== HANDLERS =====
  // (No custom handlers in this component)

  // ===== RENDER LOGIC =====
  const renderFirmwideSummary = () => (
    <Section>
      {/* Page Header */}
      <PageHeader
        title="Firmwide Summary"
        subtitle="Overview of portfolio performance and key metrics"
      />

      {/* Key Metrics Section */}
      <Section>
        <GridContainer columns={{ xs: 1, md: 3 }} spacing={3}>
          <MetricCard
            title="Total P&L"
            value={summaryData.firmwide.totalPnl || 0}
            subtitle={formatBasisPoints(
              calculateBasisPoints(summaryData.firmwide.totalPnl || 0)
            )}
            type="currency"
            color={
              (summaryData.firmwide.totalPnl || 0) < 0 ? "error" : "success"
            }
          />

          <MetricCard
            title="Long Market Value"
            value={summaryData.firmwide.totalLmv || 0}
            subtitle={`${formatPercentage(
              calculateNavPercentage(summaryData.firmwide.totalLmv || 0)
            )} of NAV`}
            type="currency"
            color="info"
          />

          <MetricCard
            title="Short Market Value"
            value={summaryData.firmwide.totalSmv || 0}
            subtitle={`-${formatPercentage(
              calculateNavPercentage(summaryData.firmwide.totalSmv || 0)
            )} of NAV`}
            type="currency"
            color="error"
          />
        </GridContainer>
      </Section>

      {/* Strategy Performance Section */}
      <Section sx={{ mt: 2, borderTop: "solid" }}>
        <Typography
          variant="h5"
          sx={{
            mb: 3,
            fontSize: { xs: "1.25rem", sm: "1.5rem" },
            fontWeight: 600,
            color: theme.palette.text.primary,
          }}
        >
          Strategy Performance
        </Typography>

        <GridContainer columns={{ xs: 1, sm: 2, lg: 3 }} spacing={4}>
          <SummaryBox title="Merger Arbitrage" data={summaryData.mab} />
          <SummaryBox title="Event-Driven Credit" data={summaryData.credit} />
          <SummaryBox title="SPAC" data={summaryData.spac} />
          <SummaryBox title="Convertible Arbitrage" data={summaryData.event} />
          <SummaryBox title="Credit Arbitrage" data={summaryData.creditArb} />
          <SummaryBox title="Hedges & Other" data={summaryData.rest} />
        </GridContainer>
      </Section>
    </Section>
  );

  const renderTabContent = () => {
    switch (shownTab) {
      case "tab1":
        return renderFirmwideSummary();
      case "tab2":
        return (
          <Section>
            <TradesTable
              data={tradedData}
              title="All Trades"
              globalFilter={tradesGlobalFilter}
              setGlobalFilter={setTradesGlobalFilter}
              sorting={tradesSorting}
              setSorting={setTradesSorting}
              scrollPosition={tradesScroll.scrollPosition}
            />
          </Section>
        );
      case "tab3":
        return (
          <Section>
            <PositionsTable
              data={allData}
              title="All Positions"
              globalFilter={GlobalFilterPositions}
              setGlobalFilter={setGlobalFilterPositions}
              sorting={positionsSorting}
              setSorting={setPositionsSorting}
              scrollPosition={positionsScroll.scrollPosition}
            />
          </Section>
        );
      case "tab4":
        return (
          <Section>
            <PortfolioAlerts
              data={allData}
              previousData={previousData}
              scrollPosition={portfolioAlertsScroll.scrollPosition}
            />
          </Section>
        );
      default:
        return null;
    }
  };

  // ===== RETURN =====
  return (
    <Box sx={{ width: "100%" }}>
      {/* Tab Navigation */}
      <CardWrapper>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <TabNavigation
              tabs={tabs}
              activeTab={shownTab}
              onTabChange={handleTabChange}
              showBadge={true}
              badgeCount={portfolioAlertsCount}
              badgeTab="tab4"
            />
          </Box>
        </Box>
      </CardWrapper>

      {/* Tab Content */}
      <Box
        sx={{
          backgroundColor: theme.palette.background.default,
        }}
      >
        {renderTabContent()}
      </Box>
    </Box>
  );
};

export default Home;
